// Presentational building blocks shared across the EventConnect pages.
import React from 'react';
import { C, EventStatus, STATUS_META } from './event';

// ── Base styles ──────────────────────────────────────────────────────────────
export const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
export const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
export const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};
export const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
  padding: '9px 20px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
export const btnGold: React.CSSProperties = {
  background: C.gold, color: C.navy, border: 'none', borderRadius: '3px',
  padding: '9px 20px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui',
};
export const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
export const btnDanger: React.CSSProperties = {
  background: C.white, color: C.danger, border: `1px solid #FECACA`,
  borderRadius: '3px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};

export function PageHeader({ title, subtitle, right }: { title: string; subtitle?: string; right?: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '20px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
      <div>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', ...style }}>{children}</div>;
}

// Pill tabs
export function Tabs<T extends string>({ tabs, active, onChange }: { tabs: readonly (readonly [T, string])[]; active: T; onChange: (t: T) => void }) {
  return (
    <div style={{ display: 'flex', gap: '4px', marginBottom: '18px', flexWrap: 'wrap' }}>
      {tabs.map(([id, label]) => (
        <button key={id} onClick={() => onChange(id)} style={{
          padding: '7px 16px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
          background: active === id ? C.navy : C.white,
          color: active === id ? C.white : C.textSec,
          border: active === id ? 'none' : `1px solid ${C.border}`,
          fontFamily: 'system-ui', fontWeight: active === id ? 600 : 500,
        }}>{label}</button>
      ))}
    </div>
  );
}

// Underline tabs (for locked/step workflows where some tabs are disabled)
export function StepTabs<T extends string>({ tabs, active, onChange }: {
  tabs: readonly { id: T; label: string; locked?: boolean }[];
  active: T; onChange: (t: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
      {tabs.map(t => {
        const on = active === t.id;
        return (
          <button key={t.id} disabled={t.locked} onClick={() => !t.locked && onChange(t.id)} style={{
            padding: '9px 16px', fontSize: '12.5px', cursor: t.locked ? 'not-allowed' : 'pointer',
            background: 'transparent', border: 'none',
            borderBottom: `2px solid ${on ? C.navy : 'transparent'}`,
            color: t.locked ? C.mutedLt : on ? C.navy : C.textSec,
            fontWeight: on ? 600 : 500, fontFamily: 'system-ui', marginBottom: '-1px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            {t.label}
            {t.locked && <span style={{ fontSize: '10px' }}>🔒</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Banner({ type, text }: { type: 'ok' | 'err' | 'info'; text: React.ReactNode }) {
  const map = {
    ok: { bg: '#F0FDF4', bd: '#BBF7D0', fg: C.success },
    err: { bg: '#FEF2F2', bd: '#FECACA', fg: C.danger },
    info: { bg: '#FEFCE8', bd: '#FDE68A', fg: C.amber },
  }[type];
  return <div style={{ background: map.bg, border: `1px solid ${map.bd}`, borderRadius: '3px', padding: '9px 12px', marginBottom: '16px', fontSize: '12.5px', color: map.fg, lineHeight: 1.5 }}>{text}</div>;
}

export function LoadingScreen({ label }: { label?: string }) {
  return <div style={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
    <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>{label || 'Loading…'}</div>
  </div>;
}

export function EmptyState({ text }: { text: string }) {
  return <div style={{ padding: '40px 18px', textAlign: 'center', fontSize: '13px', color: C.mutedLt, background: C.white, borderRadius: '4px', border: `1px solid ${C.borderLt}` }}>{text}</div>;
}

export function StatusBadge({ status }: { status: EventStatus | string }) {
  const meta = STATUS_META[status as EventStatus] || { label: status, bg: C.surface, fg: C.muted, bd: C.border };
  return <span style={{ background: meta.bg, color: meta.fg, border: `1px solid ${meta.bd}`, fontSize: '9px', fontWeight: 700, padding: '3px 8px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{meta.label}</span>;
}

export function Pill({ text, color, bg, bd }: { text: string; color: string; bg: string; bd?: string }) {
  return <span style={{ background: bg, color, border: bd ? `1px solid ${bd}` : 'none', fontSize: '9px', fontWeight: 700, padding: '3px 8px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{text}</span>;
}

export function StatCard({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub?: string; accent?: string }) {
  return (
    <div style={{ ...card, padding: '16px 18px' }}>
      <SectionLabel>{label}</SectionLabel>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '30px', fontWeight: 600, color: accent || C.navy, lineHeight: 1.1, marginTop: '8px' }}>{value}</div>
      {sub && <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, width = 560 }: { title: string; onClose: () => void; children: React.ReactNode; width?: number }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: `${width}px`, boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.borderLt}` }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '17px', color: C.navy }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: C.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '20px' }}>{children}</div>
      </div>
    </div>
  );
}

export function ProgressBar({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', margin: '4px 0 20px', flexWrap: 'wrap' }}>
      {steps.map((s, i) => {
        const done = i < current, active = i === current;
        return (
          <React.Fragment key={s}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                background: done ? C.success : active ? C.navy : C.surface,
                color: done || active ? C.white : C.mutedLt,
                border: `1px solid ${done ? C.success : active ? C.navy : C.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700,
              }}>{done ? '✓' : i + 1}</div>
              <span style={{ fontSize: '11.5px', color: active ? C.navy : done ? C.textSec : C.mutedLt, fontWeight: active ? 600 : 500, whiteSpace: 'nowrap' }}>{s}</span>
            </div>
            {i < steps.length - 1 && <div style={{ width: '28px', height: '1px', background: C.border, margin: '0 10px' }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
